import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";

import { Notification } from "@rio-cloud/rio-uikit";
import { CreateAreaReworkPayload } from "@types";

import CardCount from "src/components/CardCount";
import RoutesTable from "src/components/TableRoutes";
import { useDebounce } from "src/hooks/useDebounce";
import { excludeRoute, fetchRoutesList } from "src/services/routes";
import PageContainer from "../../components/routes/PageContainer";
import { defineAreas, defineAreasRework, defineAvoidAreas, defineRouteCost } from "../../store/routes/RoutesSlice";
import { CreateRouteCostPayload } from "../../types/costSimulator";
import { filteredRoutes } from "./components/filteredRoutes";

const PAGE_SIZE = 10;

const RoutesScreen = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [routes, setRoutes] = useState<any[]>([]);
  const [searchValue, setSearchValue] = useState("");
  const [sort, setSort] = useState("desc");
  const [fieldSort, setFieldSort] = useState("createdAt");
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [dashboard, setDashboard] = useState({
    lowestEnergyConsumer: 0,
    moreEconomics: 0,
    moreFastest: 0,
    saved: 0,
  });
  const debouncedSearch = useDebounce(searchValue, 400);
  const [search] = useSearchParams();

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;
    loadRoutesAndClean(signal);
    return () => {
      controller.abort();
    };
  }, []);

  useEffect(() => {
    loadMoreRoutes();
  }, [searchValue, sort, fieldSort]);

  const mountDashboardRoutes = (result: any) => {
    return {
      lowestEnergyConsumer: result.lowestEnergyConsumer,
      moreEconomics: result.moreEconomics,
      moreFastest: result.moreFastest,
      saved: result.saved,
    };
  }

  const loadRoutesAndClean = async (signal?: AbortSignal) => {
    try {
      const { routes, pages, result } = await searchRoutes(page, "", signal, sort, fieldSort);
      setRoutes(routes);
      setPages(pages);
      setDashboard(mountDashboardRoutes(result));
    } catch (error) {
      if ((error as any).name !== "CanceledError")
        Notification.error("Não foi possível buscar as rotas.");
    }
  }

  const searchRoutes = async (page: number, searchValue: string, signal?: AbortSignal, sort?: string, fieldSort?: string) => {
    try {
      const { data: { result } } = await fetchRoutesList(0, page * PAGE_SIZE, searchValue, sort, fieldSort, { signal });
      return { routes: result.routes, pages: result.pages, result: result };
    } catch (error) {
      Notification.error("Não foi possível buscar as viagens.");
      return { trips: [], pages: 0, result: [] };
    } finally {
      setIsLoading(false);
    }
  }

  const loadMoreRoutes = async (newPage?: number) => {
    const res = await searchRoutes(newPage || page, searchValue, undefined, sort, fieldSort);
    setRoutes(res.routes);
  }

  const handlePage = (params: { page: number }) => {
    setPage(params.page);
    loadMoreRoutes(params.page);
  }

  const handleDeleteRoute = async (id: string) => {
    try {
      await excludeRoute(id);
      Notification.info("Rota excluída com sucesso!");
      loadRoutesAndClean();
    } catch (error) {
      Notification.error("Erro ao excluir rota!");
    }
  };

  const handleCreateRoute = () => {
    dispatch(defineAreas([] as CreateAreaReworkPayload[]));
    dispatch(defineRouteCost({} as CreateRouteCostPayload))
    dispatch(defineAreasRework([]));
    dispatch(defineAvoidAreas({ formatted: undefined }));
    const url = "/routing/routes/create";
    navigate(url, { state: { edit: false, trip: false } });
  };

  const handleUpdateRoute = (id: string) => {
    const currentParams = new URLSearchParams(search);
    currentParams.set("id", id);
    const url = "/routing/routes/update?" + currentParams.toString();
    navigate(url);
  };

  const handleDownload = () => { };

  const handleSearchValueChange = async (text: string) => {
    setSearchValue(text);
    const searchValueInRoutes = routes?.filter(f => filteredRoutes(text, f))?.length > 0;
    if (text.length === 0) {
      const { routes, pages, result } = await searchRoutes(0, "", undefined, sort, fieldSort);
      setRoutes(routes);
      setPages(pages);
      setDashboard(mountDashboardRoutes(result));
    } else if (!searchValueInRoutes) {
      const { routes, pages, result } = await searchRoutes(text.length >= 0 ? -1 : page, text, undefined, sort, fieldSort);
      setRoutes(routes);
      setPages(pages);
    }
  }

  return (
    <PageContainer>
      <div>
        <div style={{ width: 400 }}>
          <CardCount
            borderColor="#30B4C0"
            label="ROTAS SALVAS"
            value={dashboard.saved}
          />
        </div>
        {/* <CardCount
          borderColor="#239B7D"
          label="MENOR CONSUMO ENERGÉTICO"
          value={dashboard.lowestEnergyConsumer}
        />
        <CardCount
          borderColor="#239B7D"
          label="MAIS ECONÔMICAS"
          value={dashboard.moreEconomics}
        />
        <CardCount
          borderColor="#239B7D"
          label="MAIS RÁPIDAS"
          value={dashboard.moreFastest}
        /> */}
      </div>
      <RoutesTable
        isLoading={isLoading}
        pageSize={PAGE_SIZE}
        showHeader
        pages={pages}
        handleDownload={handleDownload}
        handleSort={(dir, field) => {
          setSort(dir);
          setFieldSort(field);
        }}
        routes={routes}
        viewType="TABLE"
        page={page}
        handleCreateRoute={handleCreateRoute}
        handleUpdateRoute={handleUpdateRoute}
        handleSearchValueChange={handleSearchValueChange}
        handleDeleteRoute={handleDeleteRoute}
        searchValue={searchValue}
        handlePage={handlePage}
      />
    </PageContainer>
  );
};

export default RoutesScreen;
